import type { BoardRoute } from '../lib/types';
import { addressLine, distance, hhmm, hours, statusLabel, stopLabel } from '../lib/format';
import { Delta, StatusDot } from './Delta';
import { Drawer } from '@allrouted/ui/Drawer';
import { RouteMiniMap } from './RouteMiniMap';

/**
 * Route detail.
 *
 * Every stop shows the planned time struck through beside the live one, so the
 * dispatcher reads what was promised and what is now true in a single glance,
 * without arithmetic.
 */
export function RouteDrawer({
  route,
  unit,
  territoryId,
  date,
  planVersion,
  highlightStopId,
  open,
  onClose,
  onReverse,
  onToggleLock,
  onOpenOrder,
  onReplan,
}: {
  route: BoardRoute | null;
  unit: 'mi' | 'km';
  /** Which day the map should ask for road shapes for. */
  territoryId?: string | null;
  date?: string;
  planVersion?: number | null;
  highlightStopId: string | null;
  open: boolean;
  onClose: () => void;
  onReverse: () => void;
  onToggleLock: () => void;
  /**
   * Hand this one route back to the solver.
   *
   * Nothing on the board re-optimizes on its own: dragging a stop or dropping
   * an unassigned order re-times the lane around it and stops there, because a
   * dispatcher doing it by hand has already decided what they want. That leaves
   * no way to ask for the opposite on a single driver without replanning the
   * fleet -- which is what this is. The route's own stops, reordered by the
   * solver; nobody else's day moves.
   */
  onReplan?: () => void;
  /**
   * Open the order behind a stop.
   *
   * The stop is the visit; the order is everything else about it -- reference,
   * company, windows, tags, what the customer was told. A dispatcher asking
   * "why is this here" is asking about the order, and having to leave the board
   * for the Orders screen to find out loses the route they were reading.
   */
  onOpenOrder?: (orderId: string, orderName: string) => void;
}) {
  if (!route) return null;

  const done = route.stops.filter((s) => s.status === 'completed' || s.status === 'failed').length;
  const current = route.stops.find((s) => s.status === 'on_site' || s.status === 'en_route');
  const started = route.actualStart !== null;

  return (
    <Drawer
      wide
      open={open}
      onClose={onClose}
      title={route.technicianName ?? route.vehicleName}
      subtitle={`${route.vehicleName} · ${route.stops.length} stops · ${
        started ? `started ${hhmm(route.actualStart)}` : 'not started'
      }`}
      footer={
        <>
          {/*
            * The incumbent's route actions, the two of them we have a
            * counterpart for. The verb is the state it moves to, as theirs is:
            * a locked route offers Unlock.
            */}
          <button type="button" className="btn" onClick={onToggleLock}>
            {route.locked ? 'Unlock' : 'Lock'}
          </button>
          {/*
            * Unavailable while the route is locked, for the same reason a stop
            * cannot be dragged off it: reversing rewrites the order of every
            * pending stop. Disabled rather than hidden, so the reason is
            * findable -- a button that vanishes teaches nothing.
            */}
          <button
            type="button"
            className="btn"
            onClick={onReverse}
            disabled={route.locked}
            title={
              route.locked
                ? `${route.vehicleName} is locked — unlock to reverse the day`
                : 'Run the remaining stops in the opposite order'
            }
          >
            Reverse
          </button>
          {/*
            * Locked for the same reason Reverse is: it rewrites the order of
            * every pending stop, which is precisely what a lock is there to
            * prevent. A route with one stop has nothing to reorder.
            */}
          {onReplan ? (
            <button
              type="button"
              className="btn"
              onClick={onReplan}
              disabled={route.locked || route.stops.length < 2}
              title={
                route.locked
                  ? `${route.vehicleName} is locked — unlock to replan it`
                  : route.stops.length < 2
                    ? 'Nothing to reorder'
                    : `Let the solver reorder ${route.stops.length} stops on this route — no other route moves`
              }
            >
              Replan this route
            </button>
          ) : null}
          <div style={{ flex: 1 }} />
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {/* Where the route goes, before the times it goes there. */}
      <RouteMiniMap
        route={route}
        highlightStopId={highlightStopId}
        territoryId={territoryId}
        date={date}
        planVersion={planVersion}
      />

      {/*
        * Violations before the numbers, because they are the reason a
        * dispatcher opened this route. Vehicle first: a shift that does not
        * fit makes every window after it a lie.
        */}
      {route.violations.length > 0 ? (
        <div className="rd-violations">
          <b>
            {route.violationCounts.vehicle > 0
              ? `${route.violationCounts.vehicle} vehicle violation${route.violationCounts.vehicle === 1 ? '' : 's'}`
              : null}
            {route.violationCounts.vehicle > 0 && route.violationCounts.order > 0 ? ' · ' : null}
            {route.violationCounts.order > 0
              ? `${route.violationCounts.order} order violation${route.violationCounts.order === 1 ? '' : 's'}`
              : null}
          </b>
          <ul>
            {route.violations.map((v, i) => (
              <li key={i} data-scope={v.scope}>
                {v.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="stat-row">
        <Stat label="Progress" value={`${done}/${route.stops.length}`} />
        <Stat label="Running" value={started ? <Delta seconds={route.deltaSeconds} /> : '--'} />
        <Stat label="Planned end" value={hhmm(route.plannedEnd)} />
        <Stat label="Projected end" value={hhmm(route.projectedEnd)} />
        <Stat label="Distance" value={distance(route.plannedDistanceM, unit)} />
        <Stat label="Drive" value={hours(route.plannedDriveS)} />
        {/*
          * Cost is not shown here.
          *
          * It is still computed, still what the solver optimises against, and
          * still on `route.cost` for anything that needs it -- the operator
          * asked for it off the screen, not out of the system. A dispatcher
          * looking at one technician's day is deciding about that day, and a
          * figure they cannot act on invites a conversation about the number
          * rather than about the route.
          */}
      </div>

      {started && route.travelFactor !== 1 ? (
        <p style={{ color: 'var(--text-2)', margin: '0 0 14px', fontSize: 12.5 }}>
          Travel is running{' '}
          <strong style={{ color: route.travelFactor > 1 ? 'var(--warn)' : 'var(--ok)' }}>
            {Math.round(Math.abs(route.travelFactor - 1) * 100)}%{' '}
            {route.travelFactor > 1 ? 'slower' : 'faster'}
          </strong>{' '}
          than planned today. Remaining stops are forecast at that rate.
        </p>
      ) : null}

      <div className="stop-list">
        {route.stops.map((stop) => {
          /*
           * Reported first, then the geofence detection, then the forecast.
           * Saying which one is on screen matters: "the van was seen there" and
           * "the technician says they were there" are different claims, and a
           * dispatcher chasing a discrepancy needs to know which they are
           * looking at.
           */
          const reported = stop.actualArrival;
          const detectedOnly = reported === null && stop.detectedArrival !== null;
          const live = reported ?? stop.detectedArrival ?? stop.projectedArrival;
          const drifted = Math.abs(stop.deltaSeconds ?? 0) >= 60;
          return (
            <div
              key={stop.id}
              className="stop-item"
              data-current={stop.id === highlightStopId || stop.id === current?.id}
            >
              {/* `8.1`, as the driver and the dispatcher both say it. */}
              <div className="stop-seq">
                {stopLabel(stop, route.routeNumber ?? route.vehicleIdx)}
              </div>

              <div className="stop-main">
                {onOpenOrder ? (
                  <button
                    type="button"
                    className="stop-order-link"
                    onClick={() => onOpenOrder(stop.orderId, stop.orderName)}
                    title={`Open ${stop.orderName}`}
                  >
                    {stop.orderName}
                  </button>
                ) : (
                  <b>{stop.orderName}</b>
                )}
                <small>
                  {stop.customerName ? `${stop.customerName} · ` : ''}
                  {addressLine(stop.address)}
                </small>
                <small style={{ color: 'var(--text-3)' }}>
                  {statusLabel(stop.status)}
                  {stop.reasonCode ? ` · ${stop.reasonCode.replace(/_/g, ' ').toLowerCase()}` : ''}
                  {stop.timeWindows.length > 0
                    ? ` · window ${stop.timeWindows
                        .map((w) => `${hhmm(w.start)}-${hhmm(w.end)}`)
                        .join(', ')}`
                    : ''}
                </small>
              </div>

              <div className="stop-times">
                <div className="time-pair">
                  {drifted ? <div className="plan">{hhmm(stop.plannedArrival)}</div> : null}
                  <div className="live" title={detectedOnly ? 'Detected by GPS; not reported by the technician' : undefined}>
                    {hhmm(live ?? stop.plannedArrival)}
                    {detectedOnly ? <span className="detected-mark"> gps</span> : null}
                  </div>
                </div>
                <Delta seconds={stop.deltaSeconds} />
                <StatusDot status={stop.status} risk={stop.windowRisk} />
              </div>
            </div>
          );
        })}
      </div>
    </Drawer>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="stat">
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}
